import type { ContentSearchInput, PaginationConfig, SearchFilters } from '@content-search/domain';
import { toISOString, getDefaultDateRange } from '@content-search/utils';

/**
 * Options for building a ContentSearchInput object
 */
export interface BuildSearchInputOptions {
  communityId: string;
  filters: SearchFilters;
  paginationConfig: PaginationConfig;
  standardLabels: Array<{ value: string; name: string }>;
}

/**
 * Process date range filters, converting to UTC ISO string or using defaults
 */
function processDateRange(startDate?: string, endDate?: string): { startDate: string; endDate: string } {
  if (startDate && endDate) {
    return {
      startDate: toISOString(startDate),
      endDate: toISOString(endDate),
    };
  }

  const defaultRange = getDefaultDateRange();

  return {
    startDate: toISOString(defaultRange.startDate),
    endDate: toISOString(defaultRange.endDate),
  };
}

/**
 * Convert array filter to undefined if empty, otherwise return the array
 */
function normalizeArrayFilter<T>(arr: T[] | undefined): T[] | undefined {
  return arr && arr.length > 0 ? arr : undefined;
}

/**
 * Map label values to their full names using the standard labels list
 */
function mapLabelsToNames(labelValues: string[] | undefined, standardLabels: Array<{ value: string; name: string }>): string[] | undefined {
  if (!labelValues || labelValues.length === 0) {
    return undefined;
  }

  return labelValues.map(labelValue => {
    const standardLabel = standardLabels.find(label => label.value === labelValue || label.name.toLowerCase() === labelValue.toLowerCase());

    return standardLabel ? standardLabel.name : labelValue;
  });
}

/**
 * Map PolicyFilterOption to boolean violatesPolicy parameter
 */
function mapPolicyFilter(policyFilter?: string): boolean | undefined {
  if (policyFilter === 'violations') {
    return true;
  }

  if (policyFilter === 'compliant') {
    return false;
  }

  return undefined;
}

/**
 * Calculate pagination skip value
 */
function calculateSkip(currentPage: number, pageSize: number | string): number {
  const size = typeof pageSize === 'number' ? pageSize : 10;

  return (currentPage - 1) * size;
}

/**
 * Normalize language filter, converting 'all' to undefined
 */
function normalizeLanguageFilter(languageDetected?: string): string | undefined {
  return languageDetected && languageDetected !== 'all' ? languageDetected : undefined;
}

/**
 * Builds a ContentSearchInput object from UI filters and pagination config
 *
 * @param options - The options object containing all required parameters
 * @returns ContentSearchInput object ready for API consumption
 */
export function buildSearchInput(options: BuildSearchInputOptions): ContentSearchInput {
  const { communityId, filters, paginationConfig, standardLabels } = options;
  const { panelFilters, userId, subjectId } = filters;

  const metadataFilters = panelFilters.metadata && Object.keys(panelFilters.metadata).length > 0 ? panelFilters.metadata : undefined;

  const { startDate, endDate } = processDateRange(panelFilters.startDate, panelFilters.endDate);

  const contentTypes = normalizeArrayFilter(panelFilters.contentTypes);
  const topicNames = normalizeArrayFilter(panelFilters.topics);
  const labels = mapLabelsToNames(panelFilters.labels, standardLabels);
  const languageDetected = normalizeLanguageFilter(panelFilters.languageDetected);
  const violatesPolicy = mapPolicyFilter(panelFilters.policyFilter);

  const pageSize = typeof paginationConfig.pageSize === 'number' ? paginationConfig.pageSize : 10;
  const skip = calculateSkip(paginationConfig.currentPage, paginationConfig.pageSize);

  return {
    communityId,
    metadataFilters,
    subjectId,
    userId,
    startTimestamp: startDate,
    endTimestamp: endDate,
    contentType: contentTypes,
    topicName: topicNames,
    labels,
    languageDetected,
    isSampled: panelFilters.isSampled,
    violatesPolicy,
    maxResultsPerBatch: pageSize,
    skip,
  };
}
